import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Globe, Upload, PhoneCall, Users } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  useClient,
  useClientsStats,
  useInfiniteClients,
  useMyClientCounts,
  type ClientRow,
} from '@/hooks/data/useClients';
import { useInfiniteScrollSentinel } from '@/hooks/useInfiniteScrollSentinel';
import { useEffect } from 'react';
import { CreateClientDialog } from '@/components/clients/CreateClientDialog';
import { CsvImportDialog } from '@/components/clients/CsvImportDialog';
import { KundendetailsDialog } from '@/components/clients/KundendetailsDialog';
import { ClientRowActions } from '@/components/clients/ClientRowActions';
import { ClientBulkBar } from '@/components/clients/ClientBulkBar';
import { TrafficLight } from '@/components/ui/TrafficLight';
import { useSearchTracker } from '@/hooks/useSearchTracker';
import { formatDate, formatNumber } from '@/lib/formatLocale';

type Segment = 'all' | 'mine' | 'sellers' | 'buyers' | 'anrufliste' | 'kundenpool';
const SELLER_TYPES: ClientRow['client_type'][] = ['prodavac', 'najmodavac'];
/** The Käufer segment deliberately excludes `interessent`: every one of the
 *  73.167 Kundenpool rows carries that type, so including it would bury the
 *  ~800 real buyers under the cold pool. Interessenten have their own KPI
 *  tile and stay reachable through „Alle" + the type dropdown. */
const BUYER_SEGMENT_TYPES: ClientRow['client_type'][] = ['kupac', 'najmoprimac'];

interface ClientsSearch {
  segment?: Segment;
  /** Deep-link from global search — opens the detail dialog for this id. */
  open?: string;
}

export const Route = createFileRoute('/_app/clients')({
  validateSearch: (s: Record<string, unknown>): ClientsSearch => {
    const out: ClientsSearch = {};
    const seg = s.segment;
    if (
      seg === 'sellers' ||
      seg === 'buyers' ||
      seg === 'all' ||
      seg === 'mine' ||
      seg === 'anrufliste' ||
      seg === 'kundenpool'
    )
      out.segment = seg;
    if (typeof s.open === 'string' && s.open) out.open = s.open;
    return out;
  },
  component: ClientsPage,
});

const STATUS_PILL: Record<ClientRow['status'], string> = {
  otvoreno: 'fm-pill-info',
  na_cekanju: 'fm-pill-warn',
  odbijeno: 'fm-pill-dgr',
  potpisan_ugovor: 'fm-pill-pos',
  zatvoreno: 'fm-pill-dgr',
};

function ClientsPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const search0 = Route.useSearch();
  const navigate = Route.useNavigate();
  const segment: Segment = search0.segment ?? 'all';
  const setSegment = (s: Segment) => {
    setType('');
    navigate({ search: () => (s === 'all' ? {} : { segment: s }), replace: true });
  };
  const [search, setSearch] = useState('');
  const [type, setType] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  // Batch ZO #4 — traffic-light chip filter. Empty Set = show all.
  const [tlFilter, setTlFilter] = useState<Set<NonNullable<ClientRow['traffic_light']>>>(new Set());
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [viewing, setViewing] = useState<ClientRow | null>(null);

  // Deep-link from global search: /clients?open=<id> fetches the client and
  // opens its detail dialog, then strips the param so closing + reopening
  // the same result works.
  const { data: deepLinkClient } = useClient(search0.open);
  useEffect(() => {
    if (deepLinkClient) {
      setViewing(deepLinkClient);
      navigate({ search: (p) => ({ ...p, open: undefined }), replace: true });
    }
  }, [deepLinkClient, navigate]);

  const toggleTl = (k: NonNullable<ClientRow['traffic_light']>) =>
    setTlFilter((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  // "Meine" segment pushes the agentId filter to the server so the list
  // only contains rows whose assigned_agent_id matches the logged-in
  // profile. The other segments (Verkäufer, Käufer, Alle) run against
  // the full team-wide visibility from clientsWhere.
  // Batch AH faza 2 — infinite scroll. The status filter is pushed
  // server-side (added to the URL via the hook) so the next-page
  // heuristic stays honest; segment + traffic_light stay client-side
  // since segment is purely a UI grouping over client_type and
  // traffic_light is a derived join. When client-side filtering
  // narrows the visible list, the auto-fetch fallback below keeps
  // pulling next pages until either enough rows are visible or the
  // dataset ends.
  const {
    data: pageData,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteClients({
    ...(search ? { search } : {}),
    ...(type ? { clientType: type } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(segment === 'mine' && profile
      ? profile.role === 'call_center'
        ? { createdByUserId: profile.user_id }
        : { agentId: profile.id }
      : {}),
    // „Alle" now means all — the working scope, the claimable Kundenpool and
    // the Anrufliste in one list (2026-08-24, owner request). Before this the
    // default view showed 2.234 of ~83.000 people and called it „alle".
    // Verkäufer / Käufer span the same cohorts, so the chip badge, the KPI
    // tile and the list all report one number instead of three. They narrow
    // server-side: over 83k rows a client-side filter would page forever to
    // fill one screen.
    ...(segment === 'all' || segment === 'sellers' || segment === 'buyers'
      ? { audience: 'all' as const }
      : {}),
    ...(segment === 'sellers' ? { clientTypes: SELLER_TYPES as string[] } : {}),
    ...(segment === 'buyers' ? { clientTypes: BUYER_SEGMENT_TYPES as string[] } : {}),
    // „Meine" stays in the working scope on purpose: the 2026-05 import
    // round-robin-assigned thousands of Anrufliste rows to real maklers who
    // never worked them, and folding those in would bury each agent's own
    // clients. The dashboard already reports that pile separately.
    // Anrufliste = the imported Abgeber / cold-call pool. Admin-only on
    // the backend (non-admins are silently downgraded to 'normal'), so the
    // chip is only shown to admins below.
    ...(segment === 'anrufliste' ? { audience: 'imported' as const } : {}),
    // Kundenpool = claimable legacy pool (unclaimed). Visible to everyone.
    ...(segment === 'kundenpool' ? { audience: 'pool' as const } : {}),
  });
  const itemsRaw = useMemo<ClientRow[]>(
    () => pageData?.pages.flatMap((p) => p.items) ?? [],
    [pageData],
  );
  const { data: myCounts } = useMyClientCounts(!!profile?.id);

  // Narrow to the active segment client-side. When a specific raw type is
  // already chosen via the dropdown, segment narrowing is a no-op (the API
  // returned only the requested type already). Status went server-side
  // in batch AH so it's no longer applied here.
  const items = useMemo(() => {
    let base = itemsRaw;
    if (!type) {
      if (segment === 'sellers') base = base.filter((c) => SELLER_TYPES.includes(c.client_type));
      else if (segment === 'buyers')
        base = base.filter((c) => BUYER_SEGMENT_TYPES.includes(c.client_type));
    }
    if (tlFilter.size > 0)
      base = base.filter((c) => tlFilter.has((c.traffic_light ?? 'none') as NonNullable<ClientRow['traffic_light']>));
    return base;
  }, [itemsRaw, segment, type, tlFilter]);

  // Auto-fetch fallback — when client-side filters (segment/traffic_light)
  // shrink the visible list below the page size, keep fetching until we
  // either show enough or run out of pages. Prevents the sentinel from
  // never triggering when most of a 50-row page gets filtered out.
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || isLoading) return;
    if (items.length < 50) fetchNextPage();
  }, [items.length, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  // Sentinel for the normal "scroll to bottom → load more" path.
  const sentinelRef = useInfiniteScrollSentinel<HTMLDivElement>(
    () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    !!hasNextPage,
  );

  // Batch ZR (Task 3) — log search after 800ms debounce + min-length-2
  // guard. Filters captured so admin can see "searched 'Hodzic' while
  // on Verkäufer segment with status=Wartend".
  useSearchTracker(
    'clients',
    search,
    { segment, type, statusFilter, tl: Array.from(tlFilter) },
    items.length,
  );

  // Raw-enum dropdown options narrow to the segment so the operator doesn't
  // accidentally combine "Sellers" segment with a buyer-only type filter.
  const typeOptions: ClientRow['client_type'][] =
    segment === 'sellers'
      ? SELLER_TYPES
      : segment === 'buyers'
        ? BUYER_SEGMENT_TYPES
        : ['kupac', 'prodavac', 'interessent', 'najmoprimac', 'najmodavac'];

  // KPI cards — must reflect TOTAL CRM, not the paginated 100-row page.
  // /clients?limit=100 caps the list endpoint, so deriving stats from
  // `items.length` showed 100/81/18 instead of 9000+. The /clients/stats
  // endpoint runs grouped COUNT() server-side and stays cheap.
  const { data: stats } = useClientsStats();
  // 2026-08-24 — the strip headlines the REAL population. It used to show
  // the working scope (2.234) under the label „alle registrierten Kunden"
  // while the database held ~83.000 people, 73.154 of them in the
  // Kundenpool that nobody saw. Every tile now counts every cohort and
  // carries the working figure as its sub-line, so the two readings are
  // both visible and the three tiles still add up to the total.
  const allTypes = stats?.by_client_type_all ?? stats?.by_client_type;
  const workTypes = stats?.by_client_type;
  const total = stats?.total_all ?? stats?.total ?? itemsRaw.length;
  const totalWorking = stats?.total ?? 0;
  const poolCount = stats?.pool ?? 0;
  const coldCount = stats?.imported ?? 0;
  const buyers = (allTypes?.kupac ?? 0) + (allTypes?.najmoprimac ?? 0);
  const buyersWorking = (workTypes?.kupac ?? 0) + (workTypes?.najmoprimac ?? 0);
  const sellers = (allTypes?.prodavac ?? 0) + (allTypes?.najmodavac ?? 0);
  const sellersWorking = (workTypes?.prodavac ?? 0) + (workTypes?.najmodavac ?? 0);
  // Every Kundenpool row is an `interessent`, which is why this tile is
  // essentially the pool made visible.
  const interessenten = allTypes?.interessent ?? 0;
  const isAdmin = profile?.role === 'admin';
  const isCC = profile?.role === 'call_center';

  // Multi-select state for bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const allOnPageSelected = useMemo(
    () => items.length > 0 && items.every((c) => selectedIds.has(c.id)),
    [items, selectedIds],
  );
  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        items.forEach((c) => next.delete(c.id));
      } else {
        items.forEach((c) => next.add(c.id));
      }
      return next;
    });
  };
  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-ink">
            {t('clients.title', 'Kunden Management')}
          </h2>
          <p className="text-sm text-ink-3">
            {t('clients.subtitle', 'Verwalten Sie alle Ihre Kunden und deren Immobilien.')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <button
              type="button"
              onClick={() => setImporting(true)}
              className="inline-flex items-center gap-2 rounded-md border border-line bg-card px-3 py-2 text-sm font-medium text-ink transition hover:bg-paper-2"
            >
              <Upload className="h-4 w-4" />
              {t('clients.import.cta', 'CSV Importieren')}
            </button>
          )}
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper shadow-soft transition hover:bg-ink/90"
          >
            <Plus className="h-4 w-4" />
            {t('clients.create.cta', 'Kunde hinzufügen')}
          </button>
        </div>
      </header>

      {/* KPI strip */}
      <div className="fm-stagger grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('clients.stats.total', 'Gesamte Kunden')}</p>
          <p className="fm-kpi-value">{formatNumber(total)}</p>
          <p className="fm-kpi-delta">
            {t('clients.stats.total_hint_all', 'In Arbeit {{active}} · Pool {{pool}} · Anrufliste {{cold}}', {
              active: formatNumber(totalWorking),
              pool: formatNumber(poolCount),
              cold: formatNumber(coldCount),
            })}
          </p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('clients.stats.buyers', 'Käufer')}</p>
          <p className="fm-kpi-value">{formatNumber(buyers)}</p>
          <p className="fm-kpi-delta">
            {/* `n`, not `count` — i18next reserves `count` for plural rules
                and it must be a number, while this is a formatted string. */}
            {t('clients.stats.of_which_working', 'davon in Arbeit: {{n}}', {
              n: formatNumber(buyersWorking),
            })}
          </p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('clients.stats.sellers', 'Verkäufer')}</p>
          <p className="fm-kpi-value">{formatNumber(sellers)}</p>
          <p className="fm-kpi-delta">
            {t('clients.stats.of_which_working', 'davon in Arbeit: {{n}}', {
              n: formatNumber(sellersWorking),
            })}
          </p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('clients.stats.interessenten', 'Interessenten')}</p>
          <p className="fm-kpi-value">{formatNumber(interessenten)}</p>
          <p className="fm-kpi-delta">
            {t('clients.stats.interessenten_hint', 'Grösstenteils Kundenpool — noch unbearbeitet')}
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="fm-card p-5 space-y-4">
        <div>
          <h3 className="font-display text-lg text-ink">
            {t('clients.list.title', 'Kundenliste')}
          </h3>
          <p className="text-sm text-ink-3">
            {t('clients.list.subtitle', 'Suchen und verwalten Sie alle Ihre Kunden.')}
          </p>
        </div>
        {/* Segmented control — Alle / Meine / Verkäufer / Käufer.
            "Meine" pushes agentId filter to the API and shows the
            calling user's normal-scope client count (Anrufliste rows
            excluded — those are surfaced separately on the dashboard). */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1 rounded-md bg-paper-2 p-1">
            {(['all', 'mine', 'sellers', 'buyers'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSegment(s)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  segment === s ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
                }`}
              >
                {t(
                  `clients.segment.${s}`,
                  s === 'sellers'
                    ? 'Verkäufer'
                    : s === 'buyers'
                      ? 'Käufer'
                      : s === 'mine'
                        ? 'Meine'
                        : 'Alle',
                )}
                <span className="ms-1.5 rounded-full bg-paper-3 px-1.5 text-[10px]">
                  {formatNumber(
                    s === 'all'
                      ? total
                      : s === 'sellers'
                        ? sellers
                        : s === 'buyers'
                          ? buyers
                          : (myCounts?.visible ?? 0),
                  )}
                </span>
              </button>
            ))}
            {/* Anrufliste = imported Abgeber / cold-call pool. Visible to
                admin + call-center (backend gates 'imported' to those roles
                and downgrades anyone else to 'normal'). No count badge —
                the pool is global while my-counts is per-user. */}
            {(isAdmin || isCC) && (
              <button
                type="button"
                onClick={() => setSegment('anrufliste')}
                title={t(
                  'clients.segment.anrufliste_hint',
                  'Importierte Abgeber-/Cold-Call-Klienten (Anrufliste · Sophie-Pool)',
                )}
                className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  segment === 'anrufliste'
                    ? 'bg-card text-ink shadow-soft'
                    : 'text-ink-3 hover:text-ink'
                }`}
              >
                <PhoneCall className="h-3 w-3" />
                {t('clients.segment.anrufliste', 'Anrufliste')}
                <span className="ms-1.5 rounded-full bg-paper-3 px-1.5 text-[10px]">
                  {formatNumber((stats?.imported ?? 0))}
                </span>
              </button>
            )}
            {/* Kundenpool = claimable legacy-client pool. Visible to all
                roles (maker/CC/admin) — anyone may browse + claim. */}
            <button
              type="button"
              onClick={() => setSegment('kundenpool')}
              title={t(
                'clients.segment.kundenpool_hint',
                'Klienten-Pool: alte 4M-Datenbanken zum Übernehmen / Zuweisen',
              )}
              className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                segment === 'kundenpool'
                  ? 'bg-card text-ink shadow-soft'
                  : 'text-ink-3 hover:text-ink'
              }`}
            >
              <Users className="h-3 w-3" />
              {t('clients.segment.kundenpool', 'Kundenpool')}
              <span className="ms-1.5 rounded-full bg-paper-3 px-1.5 text-[10px]">
                {formatNumber((stats?.pool ?? 0))}
              </span>
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t(
                'clients.search.placeholder',
                'Suchen nach Name, Telefon, Standort, Plattform…',
              )}
              className="w-full rounded-md border border-line bg-card ps-9 pe-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            />
          </div>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          >
            <option value="">{t('clients.filters.all_types', 'Alle Typen')}</option>
            {typeOptions.map((v) => (
              <option key={v} value={v}>
                {t(`enums.client_type.${v}`, v)}
              </option>
            ))}
          </select>
          {/* Batch ZO #5 — Status filter dropdown */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          >
            <option value="">{t('clients.filters.all_status', 'Alle Status')}</option>
            <option value="otvoreno">{t('enums.client_status.otvoreno', 'Offen')}</option>
            <option value="na_cekanju">{t('enums.client_status.na_cekanju', 'Wartend')}</option>
            <option value="odbijeno">{t('enums.client_status.odbijeno', 'Abgelehnt')}</option>
            <option value="potpisan_ugovor">{t('enums.client_status.potpisan_ugovor', 'Vertrag unterzeichnet')}</option>
            <option value="zatvoreno">{t('enums.client_status.zatvoreno', 'Geschlossen')}</option>
          </select>
        </div>

        {/* Batch ZO #4 — traffic-light filter chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink-3">{t('common.filter_by_light', 'Ampel')}:</span>
          {(['green', 'yellow', 'red', 'off', 'none'] as const).map((k) => {
            const active = tlFilter.has(k);
            const dotColor =
              k === 'green'
                ? 'bg-emerald-500'
                : k === 'yellow'
                  ? 'bg-amber-400'
                  : k === 'red'
                    ? 'bg-red-500'
                    : k === 'off'
                      ? 'bg-slate-400'
                      : 'bg-slate-200';
            return (
              <button
                key={k}
                type="button"
                onClick={() => toggleTl(k)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                  active
                    ? 'border-[hsl(var(--brand-gold-deep))] bg-[hsl(var(--brand-gold)/0.12)] text-ink'
                    : 'border-line bg-card text-ink-3 hover:text-ink'
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${dotColor}`} />
                {t(`common.traffic_light.${k}`, k)}
              </button>
            );
          })}
          {tlFilter.size > 0 && (
            <button
              type="button"
              onClick={() => setTlFilter(new Set())}
              className="text-xs text-ink-3 underline hover:text-ink"
            >
              {t('common.reset', 'Zurücksetzen')}
            </button>
          )}
        </div>

        {/* Mobile card list */}
        <div className="space-y-2 md:hidden">
          {isLoading && (
            <p className="py-12 text-center text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
          )}
          {error && (
            <p className="py-12 text-center text-sm text-[hsl(var(--danger))]">{(error as Error).message}</p>
          )}
          {!isLoading && !error && items.length === 0 && (
            <p className="py-12 text-center text-sm text-ink-3">
              {t('clients.empty', 'Keine Klienten gefunden.')}
            </p>
          )}
          {items.map((c) => {
            const fullName = [c.title, c.first_name, c.last_name].filter(Boolean).join(' ');
            return (
              <article
                key={c.id}
                className="cursor-pointer rounded-md border border-line bg-card p-3"
                onClick={() => setViewing(c)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <TrafficLight state={c.traffic_light} />
                      <p className="font-medium text-ink truncate">{fullName || '—'}</p>
                    </div>
                    <p className="mt-0.5 font-mono text-xs text-ink-2">{c.phone}</p>
                  </div>
                  <span className={`fm-pill shrink-0 ${STATUS_PILL[c.status] ?? 'fm-pill-neutral'}`}>
                    {t(`enums.client_status.${c.status}`, c.status)}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                  <span
                    className={`fm-pill ${
                      SELLER_TYPES.includes(c.client_type)
                        ? 'fm-pill-seller'
                        : 'fm-pill-buyer'
                    }`}
                  >
                    {t(`enums.client_type.${c.client_type}`, c.client_type)}
                  </span>
                  {c.platform && (
                    <span className="inline-flex items-center gap-1 text-ink-2">
                      <Globe className="h-3 w-3 text-ink-3" />
                      {c.platform}
                    </span>
                  )}
                  <span className="ms-auto text-ink-3">{formatDate(c.created_at)}</span>
                </div>
                <div
                  className="mt-2 flex justify-end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ClientRowActions client={c} isAdmin={isAdmin} />
                </div>
              </article>
            );
          })}
        </div>

        {/* Desktop table */}
        <div className="fm-table-wrap hidden md:block">
          <table className="fm-table">
            <thead>
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    aria-label={t('clients.bulk.select_all', 'Alle auswählen')}
                    checked={allOnPageSelected}
                    onChange={toggleAllOnPage}
                    className="cursor-pointer"
                  />
                </th>
                <th>{t('clients.cols.name', 'Name')}</th>
                <th>{t('clients.cols.type', 'Typ')}</th>
                <th>{t('clients.cols.status', 'Status')}</th>
                <th>{t('clients.cols.phone', 'Telefon')}</th>
                <th>{t('clients.cols.phone2', 'Phone 2')}</th>
                <th>{t('clients.cols.platform', 'Plattform')}</th>
                <th>{t('clients.cols.created', 'Erstellt')}</th>
                <th className="text-end">{t('clients.cols.actions', 'Aktionen')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={9} className="text-center text-ink-3">
                    {t('common.loading', 'Lädt …')}
                  </td>
                </tr>
              )}
              {error && (
                <tr>
                  <td colSpan={8} className="text-center text-[hsl(var(--danger))]">
                    {(error as Error).message}
                  </td>
                </tr>
              )}
              {!isLoading && !error && items.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-ink-3">
                    {t('clients.empty', 'Keine Klienten gefunden.')}
                  </td>
                </tr>
              )}
              {items.map((c) => {
                const fullName = [c.title, c.first_name, c.last_name].filter(Boolean).join(' ');
                return (
                  <tr
                    key={c.id}
                    className={`cursor-pointer ${selectedIds.has(c.id) ? 'bg-[hsl(var(--brand-gold)/0.06)]' : ''}`}
                    onClick={() => setViewing(c)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(c.id)}
                        onChange={() => toggleOne(c.id)}
                        className="cursor-pointer"
                        aria-label={t('clients.bulk.select_one', 'Auswählen')}
                      />
                    </td>
                    <td className="font-medium text-ink">
                      <span className="inline-flex items-center gap-2">
                        <TrafficLight state={c.traffic_light} />
                        {fullName || '—'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`fm-pill ${
                          SELLER_TYPES.includes(c.client_type)
                            ? 'fm-pill-seller'
                            : 'fm-pill-buyer'
                        }`}
                      >
                        {t(`enums.client_type.${c.client_type}`, c.client_type)}
                      </span>
                    </td>
                    <td>
                      <span className={`fm-pill ${STATUS_PILL[c.status] ?? 'fm-pill-neutral'}`}>
                        {t(`enums.client_status.${c.status}`, c.status)}
                      </span>
                    </td>
                    <td className="font-mono text-xs">{c.phone}</td>
                    <td className="font-mono text-xs text-ink-3">{c.phone_2 ?? '-'}</td>
                    <td>
                      {c.platform ? (
                        <div className="flex flex-col">
                          <span className="inline-flex items-center gap-1 text-sm text-ink-2">
                            <Globe className="h-3 w-3 text-ink-3" />
                            {c.platform}
                          </span>
                          {c.platform_code && (
                            <span className="font-mono text-[10px] text-ink-3 truncate max-w-[200px]">
                              {c.platform_code}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-ink-3">N/A</span>
                      )}
                    </td>
                    <td className="text-xs text-ink-3">{formatDate(c.created_at)}</td>
                    <td className="text-end">
                      <ClientRowActions client={c} isAdmin={isAdmin} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Infinite scroll footer */}
        {items.length > 0 && (
          <>
            {hasNextPage && (
              <div
                ref={sentinelRef}
                className="py-6 text-center text-xs text-ink-3"
              >
                {isFetchingNextPage
                  ? t('common.loading_more', 'Lädt weitere …')
                  : ''}
              </div>
            )}
            {!hasNextPage && (
              <div className="border-t border-line pt-3 text-center text-[11px] uppercase tracking-wide text-ink-3/60">
                {t('common.end_of_list', 'Ende der Liste')}
              </div>
            )}
          </>
        )}
      </div>

      <ClientBulkBar
        selectedIds={selectedIds}
        selectedClients={items.filter((c) => selectedIds.has(c.id))}
        onClear={clearSelection}
      />

      {/* dialogs */}
      <CreateClientDialog open={creating} onOpenChange={setCreating} />
      <CsvImportDialog open={importing} onOpenChange={setImporting} />
      {viewing && (
        <KundendetailsDialog
          client={viewing}
          open={true}
          onOpenChange={(o) => !o && setViewing(null)}
        />
      )}
    </section>
  );
}

